// Chat actions drawer — opens from the chat header "…" button and the
// sidebar gear. Right-side drawer on desktop, bottom sheet on mobile.
// Contains: Mode picker, Manage skills, Memory ops (compact / manage /
// presets), Session ops (share / export / clear), and a footer showing
// who's signed in + model + cwd (server-driven values).

import { useEffect } from "preact/hooks";
import type { ComponentChildren } from "preact";
import { Icon, type IconName } from "../../icons";
import type { Me } from "../../lib/use-me";
import { MOCK_CHAT_MODES, type ChatMode } from "./mock";
import { MODE_LABEL, MODE_DESCRIPTION } from ".";

interface ActionsMenuProps {
  open: boolean;
  onClose: () => void;
  mode: ChatMode;
  setMode: (m: ChatMode) => void;
  onClearChat: () => void;
  onDeleteThread: () => void;
  onCompact: () => void;
  onShare: () => void;
  onExport: () => void;
  onManageSkills: () => void;
  onManageMemory: () => void;
  onManagePresets: () => void;
  activePresetName: string | null;
  notify: (msg: string) => void;
  me: Me | null;
  model: string;
  cwd: string;
}

export function ChatActionsMenu({
  open, onClose, mode, setMode, onClearChat, onDeleteThread, onCompact, onShare, onExport,
  onManageSkills, onManageMemory, onManagePresets,
  activePresetName, notify, me, model, cwd,
}: ActionsMenuProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  const todo = (label: string) => () => notify(`${label} — coming soon`);

  return (
    <div className="chat-menu-bg" onClick={onClose}>
      <aside
        className="chat-menu"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Chat actions"
      >
        <header className="cm-head">
          <span style={{ fontFamily: "var(--f-display)", fontWeight: 600, fontSize: 14 }}>
            Chat actions
          </span>
          <button className="icon-btn" onClick={onClose}><Icon name="x" size={14} /></button>
        </header>

        <div className="cm-body">
          <Section>Mode</Section>
          <div className="cm-mode">
            {MOCK_CHAT_MODES.map((m) => (
              <button
                key={m}
                data-active={mode === m}
                onClick={() => setMode(m)}
                title={MODE_DESCRIPTION[m]}
              >
                {MODE_LABEL[m]}
              </button>
            ))}
          </div>
          <p className="cm-mode-desc">{MODE_DESCRIPTION[mode]}</p>

          <Section>Skills</Section>
          <Row
            icon="sparkle"
            label="Manage skills"
            sub="What the agent can do"
            onClick={() => { onClose(); onManageSkills(); }}
          />

          <Section>Memory</Section>
          <Row
            icon="compact"
            label="Compact this chat"
            sub="Summarize older messages to free context"
            onClick={() => { onClose(); onCompact(); }}
          />
          <Row
            icon="memory"
            label="Manage memory"
            sub="What this assistant remembers about you"
            onClick={() => { onClose(); onManageMemory(); }}
          />
          <Row
            icon="edit"
            label="Prompt presets"
            sub={activePresetName ? `active · ${activePresetName}` : "Reusable system-prompt snippets"}
            onClick={() => { onClose(); onManagePresets(); }}
          />

          <Section>Session</Section>
          <Row
            icon="share"
            label="Share session"
            sub="Read-only link anyone with it can open"
            onClick={() => { onClose(); onShare(); }}
          />
          <Row
            icon="doc"
            label="Export as Markdown"
            sub="Save the whole conversation"
            onClick={() => { onClose(); onExport(); }}
          />
          <Row
            icon="trash"
            label="Clear conversation"
            sub="Empty the messages but keep the thread"
            tone="danger"
            onClick={() => { onClearChat(); onClose(); }}
          />
          <Row
            icon="trash"
            label="Delete chat"
            sub="Permanently remove this thread and its history"
            tone="danger"
            onClick={() => { onClose(); onDeleteThread(); }}
          />
        </div>

        <footer className="cm-foot">
          <div>
            signed in as: <strong style={{ color: "var(--text)" }}>{me?.username ?? "—"}</strong>{" "}
            ({me?.role ?? "—"})
          </div>
          <div>model: <strong style={{ color: "var(--text)" }}>{model}</strong></div>
          <div>cwd: <span style={{ color: "var(--text-mute)" }}>{cwd}</span></div>
        </footer>
      </aside>
    </div>
  );
}

function Section({ children }: { children: ComponentChildren }) {
  return <div className="cm-sect">{children}</div>;
}

function Row({
  icon, label, sub, tone, onClick,
}: {
  icon: IconName;
  label: string;
  sub?: string;
  tone?: "danger";
  onClick?: () => void;
}) {
  return (
    <button className="cm-row" data-tone={tone || ""} onClick={onClick}>
      <span className="cm-ico"><Icon name={icon} size={15} /></span>
      <span>
        <div className="cm-lbl">{label}</div>
        {sub && <div className="cm-sub">{sub}</div>}
      </span>
    </button>
  );
}
